import React, { useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../constants';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import withAuth from '../../auth';
import Spinner from 'react-bootstrap/Spinner';
import NPPlacesInput from '../google_map/NPPlacesInput';
import NPMapViewDirections from '../google_map/NPMapViewDirections';
import {normalizeMapPinCoordinate} from '../google_map/mapPinUtils';
import { GoogleMapsProvider } from '../google_map/GoogleMapsContext';
import InputMask from "react-input-mask";
import moment from "moment";
import {appendPickupLocationFormData, isMapPinLocation} from './pickupLocationFormData';

const PickupUpdate = () => {
    const { pickupId } = useParams();

    const [pickupOriginal, setPickupOriginal] = useState(null);
    const [user, setUser] = useState(null);

    const [routes, setRoutes] = useState([
        { id: 1, origin: {}, dropoff: {}, originUseMyself: false, dropoffUseMyself: false },
    ]);
    const [distance, setDistance] = useState(0);

    const [additionalInfo, setAdditionalInfo] = useState("");
    const additionalInfoRef = useRef(null);

    const [truckType, setTruckType] = useState(1);
    const [cargoInsurance, setCargoInsurance] = useState(6);
    const [items, setItems] = useState([]);
    const [pickup, setPickup] = useState({ origins: [], dropoffs: [] });
    const [optimizedWaypoints, setOptimizedWaypoints] = useState([]);

    const [isScheduled, setIsScheduled] = useState(false);
    const [scheduledPickupTime, setScheduledPickupTime] = useState('');

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    const { token, setToken, hasPermission } = useContext(AuthContext);
    const navigate = useNavigate();

    const itemIdCounter = useRef(0);
    const itemDetailsRefs = useRef({});

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const minDateTime = `${year}-${month}-${day}T00:00`;

    const [priceEstimate, setPriceEstimate] = useState(null);
    const [isEstimating, setIsEstimating] = useState(false);
    const [estimateError, setEstimateError] = useState('');

    const [itemSuggestions, setItemSuggestions] = useState([]);
    const [activeItemId, setActiveItemId] = useState(null);
    const searchTimeoutRef = useRef(null);
    const [photoFetchLoadingItemId, setPhotoFetchLoadingItemId] = useState(null);

    const [isLoading, setIsLoading] = useState(false);

    // -------------------- Load pickup --------------------
    useEffect(() => {
        if (!pickupId) return;

        setIsLoading(true);

        axios.get(`${API_URL}/api/admin/pickups/${pickupId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                const p = response.data.data;
                setPickupOriginal(p);
                setUser(p.user || null);

                // Build routes from pickup.origins/dropoffs
                const originCount = p.origins?.length || 0;
                const dropoffCount = p.dropoffs?.length || 0;
                const maxRoutes = Math.max(originCount, dropoffCount, 1);

                const mappedRoutes = [];
                for (let i = 0; i < maxRoutes; i++) {
                    const origin = i < originCount ? p.origins[i] : (p.origins[originCount - 1] || {});
                    const dropoff = i < dropoffCount ? p.dropoffs[i] : (p.dropoffs[dropoffCount - 1] || {});

                    mappedRoutes.push({
                        id: i + 1,
                        origin: {
                            address: origin.address || "",
                            place_name: origin.place_name || "",
                            latitude: origin.latitude ?? "",
                            longitude: origin.longitude ?? "",
                            is_map_pin: isMapPinLocation(origin),
                            contact_name: origin.contact_name || "",
                            contact_phone: origin.contact_phone || "",
                        },
                        dropoff: {
                            address: dropoff.address || "",
                            place_name: dropoff.place_name || "",
                            latitude: dropoff.latitude ?? "",
                            longitude: dropoff.longitude ?? "",
                            is_map_pin: isMapPinLocation(dropoff),
                            contact_name: dropoff.contact_name || "",
                            contact_phone: dropoff.contact_phone || "",
                        },
                        originUseMyself: false,
                        dropoffUseMyself: false,
                    });
                }

                setRoutes(mappedRoutes);

                // Items
                itemIdCounter.current = 0;
                const mappedItems = (p.items || []).map((it, index) => {
                    const nextId = itemIdCounter.current++;
                    return {
                        id: nextId,
                        item_name: it.item_name || "",
                        weight: it.weight || "0-200Lbs",
                        size: it.size || "Small",
                        details: it.details || "",
                        purchase_order_number: it.purchase_order_number || "",
                        routeId: index + 1, // same approach as duplicate create
                        photo: null,
                        photoUrl: it.photo || null, // show existing photo as preview
                    };
                });

                setItems(mappedItems.length ? mappedItems : [{
                    id: itemIdCounter.current++,
                    item_name: "",
                    weight: "0-200Lbs",
                    size: "Small",
                    details: "",
                    purchase_order_number: "",
                    routeId: 1,
                    photo: null,
                    photoUrl: null,
                }]);

                setPickup({
                    origins: p.origins || [],
                    dropoffs: p.dropoffs || [],
                });

                setTruckType(p.truck_type?.id || 1);
                setCargoInsurance(p.cargo_insurance?.id || 6);
                setAdditionalInfo(p.additional_info || "");

                setIsScheduled(!!p.scheduled_pickup_time);
                let scheduledTime = p.scheduled_pickup_time ? new Date(p.scheduled_pickup_time) : null;
                const now = new Date();
                if (scheduledTime && scheduledTime < now) {
                    scheduledTime = new Date(now.getTime() + 24 * 60 * 60 * 1000);
                }
                setScheduledPickupTime(scheduledTime ? moment(scheduledTime).format("YYYY-MM-DDTHH:mm") : '');
            })
            .catch((err) => {
                console.error('Error fetching pickup:', err);
                if (err.response && err.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                } else {
                    setError('Failed to load delivery.');
                }
            })
            .finally(() => setIsLoading(false));
    }, [pickupId, token]);

    // -------------------- Helpers --------------------
    const invalidateRouteCalculation = () => {
        setDistance(0);
        setOptimizedWaypoints([]);
    };

    const handlePickupSelect = (id, address, location, placeName = null, isMapPin = false) => {
        const coordinate = normalizeMapPinCoordinate(location);
        if (!coordinate) return;
        const {lat: latitude, lng: longitude} = coordinate;
        invalidateRouteCalculation();

        const newRoutes = routes.map((route) =>
            route.id === id
                ? {
                    ...route,
                    origin: {
                        ...route.origin,
                        address,
                        place_name: placeName || null,
                        latitude,
                        longitude,
                        is_map_pin: isMapPin,
                    },
                }
                : route
        );

        setRoutes(newRoutes);
        setPickup((prev) => ({
            ...prev,
            origins: newRoutes.map((r) => r.origin),
        }));
    };

    const handleDropoffSelect = (id, address, location, placeName = null, isMapPin = false) => {
        const coordinate = normalizeMapPinCoordinate(location);
        if (!coordinate) return;
        const {lat: latitude, lng: longitude} = coordinate;
        invalidateRouteCalculation();

        const newRoutes = routes.map((route) =>
            route.id === id
                ? {
                    ...route,
                    dropoff: {
                        ...route.dropoff,
                        address,
                        place_name: placeName || null,
                        latitude,
                        longitude,
                        is_map_pin: isMapPin,
                    },
                }
                : route
        );

        setRoutes(newRoutes);
        setPickup((prev) => ({
            ...prev,
            dropoffs: newRoutes.map((r) => r.dropoff),
        }));
    };

    const handleAddRoute = () => {
        const newRouteId = routes.length ? routes[routes.length - 1].id + 1 : 1;
        setRoutes([
            ...routes,
            { id: newRouteId, origin: {}, dropoff: {}, originUseMyself: false, dropoffUseMyself: false },
        ]);
    };

    const handleRemoveRoute = (id) => {
        const newRoutes = routes.filter((r) => r.id !== id);
        setRoutes(newRoutes);

        const newItems = items.filter((it) => it.routeId !== id);
        setItems(newItems);

        setPickup((prev) => ({
            ...prev,
            origins: newRoutes.map((r) => r.origin),
            dropoffs: newRoutes.map((r) => r.dropoff),
        }));
    };

    const handleRouteChange = (id, field, value, isOrigin = true) => {
        const newRoutes = routes.map((route) =>
            route.id === id
                ? {
                    ...route,
                    [isOrigin ? 'origin' : 'dropoff']: {
                        ...route[isOrigin ? 'origin' : 'dropoff'],
                        [field]: value,
                    },
                }
                : route
        );

        setRoutes(newRoutes);
        setPickup((prev) => ({
            ...prev,
            origins: newRoutes.map((r) => r.origin),
            dropoffs: newRoutes.map((r) => r.dropoff),
        }));
    };

    const handleLocationAddressChange = (id, address, isOrigin = true) => {
        invalidateRouteCalculation();
        const locationKey = isOrigin ? 'origin' : 'dropoff';
        const newRoutes = routes.map((route) =>
            route.id === id
                ? {
                    ...route,
                    [locationKey]: {
                        ...route[locationKey],
                        address,
                        place_name: null,
                        latitude: '',
                        longitude: '',
                        is_map_pin: false,
                    },
                }
                : route
        );

        setRoutes(newRoutes);
        setPickup((prev) => ({
            ...prev,
            origins: newRoutes.map((route) => route.origin),
            dropoffs: newRoutes.map((route) => route.dropoff),
        }));
    };

    const handleWaypointsOptimized = (waypoints) => {
        setOptimizedWaypoints(waypoints);
    };

    const handleAddItem = () => {
        if (!routes.length) return alert('Please add a route before adding items.');

        setItems([
            ...items,
            {
                id: itemIdCounter.current++,
                item_name: '',
                weight: '0-200Lbs',
                size: 'Small',
                details: '',
                purchase_order_number: '',
                routeId: routes[0].id,
                photo: null,
                photoUrl: null,
            },
        ]);
    };

    const handleRemoveItem = (id) => {
        setItems((prev) => prev.filter((it) => it.id !== id));
        delete itemDetailsRefs.current[id];
    };

    const handleItemChange = (id, field, value) => {
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
    };

    const handleItemRouteChange = (id, newRouteId) => {
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, routeId: newRouteId } : it)));
    };

    const handleFileChange = (id, file) => {
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            setItems((prev) =>
                prev.map((it) =>
                    it.id === id
                        ? { ...it, photo: file, photoUrl: e.target.result }
                        : it
                )
            );
        };
        reader.readAsDataURL(file);
    };

    const handleUseMyselfChange = (id, isOrigin, useMyself) => {
        setRoutes((prevRoutes) =>
            prevRoutes.map((route) => {
                if (route.id !== id) return route;

                const updated = { ...route };
                if (isOrigin) {
                    updated.originUseMyself = useMyself;
                    if (useMyself && user) {
                        updated.origin.contact_name = `${user.first_name} ${user.last_name}`;
                        updated.origin.contact_phone = user.phone_num;
                    } else {
                        updated.origin.contact_name = '';
                        updated.origin.contact_phone = '';
                    }
                } else {
                    updated.dropoffUseMyself = useMyself;
                    if (useMyself && user) {
                        updated.dropoff.contact_name = `${user.first_name} ${user.last_name}`;
                        updated.dropoff.contact_phone = user.phone_num;
                    } else {
                        updated.dropoff.contact_name = '';
                        updated.dropoff.contact_phone = '';
                    }
                }
                return updated;
            })
        );
    };

    useEffect(() => {
        if (additionalInfoRef.current) {
            additionalInfoRef.current.style.height = "auto";
            additionalInfoRef.current.style.height = `${additionalInfoRef.current.scrollHeight}px`;
        }
    }, [additionalInfo]);

    useEffect(() => {
        items.forEach((it) => {
            const textarea = itemDetailsRefs.current[it.id];
            if (textarea) {
                textarea.style.height = 'auto';
                textarea.style.height = `${textarea.scrollHeight}px`;
            }
        });
    }, [items]);

    // -------------------- Price preview --------------------
    const lastDropoff = pickup.dropoffs[pickup.dropoffs.length - 1];
    const lastDropoffLat = lastDropoff?.latitude;
    const lastDropoffLng = lastDropoff?.longitude;

    useEffect(() => {
        if (distance > 0 && truckType && cargoInsurance && lastDropoffLat && lastDropoffLng) {
            const requestData = {
                distance,
                truck_type_id: truckType,
                cargo_insurance_id: cargoInsurance,
                dropoff_lat: lastDropoffLat,
                dropoff_long: lastDropoffLng,
            };
            if (isScheduled && scheduledPickupTime) {
                requestData.scheduled_pickup_time = scheduledPickupTime;
            }

            setIsEstimating(true);
            setEstimateError('');

            axios.post(`${API_URL}/api/admin/pickups/price-preview`, requestData, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then((res) => setPriceEstimate(res.data))
                .catch((err) => {
                    console.error('Price preview error:', err);
                    if (err.response?.status === 401) {
                        localStorage.removeItem('authToken');
                        setToken(null);
                        navigate('/');
                    } else if (err?.response?.data?.errors) {
                        const errors = err.response.data.errors;
                        const msg = Object.keys(errors).map(k => errors[k].join(' ')).join(' ');
                        setEstimateError(msg);
                    } else if (err?.response?.data?.message) {
                        setEstimateError(err.response.data.message);
                    } else {
                        setEstimateError('Failed to fetch price estimate.');
                    }
                })
                .finally(() => setIsEstimating(false));
        } else {
            setPriceEstimate(null);
        }
    }, [distance, truckType, cargoInsurance, lastDropoffLat, lastDropoffLng, isScheduled, scheduledPickupTime, token]);

    // -------------------- Item suggestions --------------------
    const handleItemNameChange = (itemId, search_name) => {
        setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, item_name: search_name } : it)));
        setActiveItemId(itemId);

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

        searchTimeoutRef.current = setTimeout(async () => {
            if (!search_name || search_name.length < 1) {
                setItemSuggestions([]);
                return;
            }
            try {
                const response = await axios.get(`${API_URL}/api/admin/pickups/items/suggestions`, {
                    params: { query: search_name },
                    headers: { Authorization: `Bearer ${token}` },
                });
                setItemSuggestions(response.data);
            } catch (e) {
                console.error('Suggestion fetch error:', e);
                setItemSuggestions([]);
            }
        }, 300);
    };

    const handleSuggestionSelect = async (itemId, suggestion) => {
        setPhotoFetchLoadingItemId(itemId);

        setItems((prev) =>
            prev.map((it) => {
                if (it.id !== itemId) return it;
                return {
                    ...it,
                    item_name: suggestion.item_name,
                    weight: suggestion.weight || it.weight,
                    details: suggestion.details || it.details,
                    size: suggestion.size || it.size,
                    photo: null,
                    photoUrl: suggestion.photo,
                };
            })
        );

        setItemSuggestions([]);
        setActiveItemId(null);
        setPhotoFetchLoadingItemId(null);
    };

    // -------------------- Submit update --------------------
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!hasPermission?.('pickup-update')) {
            setError('You do not have permission to edit delivery details.');
            return;
        }

        if (isScheduled && !scheduledPickupTime) {
            setError('Please select a scheduled delivery time.');
            return;
        }
        if (isScheduled && new Date(scheduledPickupTime) < new Date()) {
            setError('Scheduled pickup time cannot be in the past.');
            return;
        }

        const hasCompleteRoutes = routes.length > 0 && routes.every((route) => (
            String(route.origin.address ?? '').trim()
            && normalizeMapPinCoordinate(route.origin)
            && String(route.dropoff.address ?? '').trim()
            && normalizeMapPinCoordinate(route.dropoff)
        ));
        if (!hasCompleteRoutes) {
            setError('Please select valid pickup and drop-off locations for every route.');
            return;
        }
        if (!Number.isFinite(Number(distance)) || Number(distance) <= 0) {
            setError('Please wait while we calculate a drivable route between these locations.');
            return;
        }

        const itemsWithRoutes = items
            .map((it) => {
                const route = routes.find((r) => r.id === it.routeId);
                if (!route) return null;

                return {
                    item_name: it.item_name,
                    size: it.size,
                    weight: it.weight,
                    purchase_order_number: it.purchase_order_number,
                    details: it.details,
                    origin: {
                        latitude: route.origin.latitude,
                        longitude: route.origin.longitude,
                        address: route.origin.address,
                        place_name: route.origin.place_name,
                        is_map_pin: route.origin.is_map_pin,
                        contact_name: route.origin.contact_name,
                        contact_phone: route.origin.contact_phone,
                    },
                    dropoff: {
                        latitude: route.dropoff.latitude,
                        longitude: route.dropoff.longitude,
                        address: route.dropoff.address,
                        place_name: route.dropoff.place_name,
                        is_map_pin: route.dropoff.is_map_pin,
                        contact_name: route.dropoff.contact_name,
                        contact_phone: route.dropoff.contact_phone,
                    },
                    photo: it.photo,
                    photoUrl: it.photoUrl,
                };
            })
            .filter(Boolean);

        const formData = new FormData();

        // Core pickup fields
        formData.append("add_info", additionalInfo || "");
        formData.append("distance", distance);

        formData.append('origin_lat', pickup.origins[0]?.latitude ?? '');
        formData.append('origin_long', pickup.origins[0]?.longitude ?? '');
        formData.append('origin_address', pickup.origins[0]?.address || '');

        formData.append('dropoff_lat', pickup.dropoffs[pickup.dropoffs.length - 1]?.latitude ?? '');
        formData.append('dropoff_long', pickup.dropoffs[pickup.dropoffs.length - 1]?.longitude ?? '');
        formData.append('dropoff_address', pickup.dropoffs[pickup.dropoffs.length - 1]?.address || '');

        formData.append('truck_type_id', truckType);
        formData.append('cargo_insurance_id', cargoInsurance);

        if (isScheduled && scheduledPickupTime) {
            formData.append('scheduled_pickup_time', scheduledPickupTime);
        }

        // Items
        itemsWithRoutes.forEach((item, index) => {
            formData.append(`items[${index}][item_name]`, item.item_name);
            formData.append(`items[${index}][weight]`, item.weight);
            formData.append(`items[${index}][size]`, item.size);
            formData.append(`items[${index}][purchase_order_number]`, item.purchase_order_number || '');
            formData.append(`items[${index}][details]`, item.details || '');

            appendPickupLocationFormData(formData, `items[${index}][origin]`, item.origin);
            appendPickupLocationFormData(formData, `items[${index}][dropoff]`, item.dropoff);

            if (item.photo && item.photo instanceof File) {
                formData.append(`items[${index}][photo]`, item.photo);
            } else if (item.photoUrl) {
                // your admin update supports photo_url
                formData.append(`items[${index}][photo_url]`, item.photoUrl);
            }
        });

        pickup.origins.forEach((origin, index) => {
            appendPickupLocationFormData(formData, `origins[${index}]`, origin);
        });

        pickup.dropoffs.forEach((dropoff, index) => {
            appendPickupLocationFormData(formData, `dropoffs[${index}]`, dropoff);
        });

        if (optimizedWaypoints && optimizedWaypoints.length > 0) {
            optimizedWaypoints.forEach((wp, index) => {
                formData.append(`optimizedWaypoints[${index}][location][lat]`, wp.location.lat());
                formData.append(`optimizedWaypoints[${index}][location][lng]`, wp.location.lng());
            });
        }

        setError('');
        setIsSubmitted(true);

        try {
            await axios.post(`${API_URL}/api/admin/pickups/${pickupId}/update`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            navigate(`/pickups/${pickupId}`);
        } catch (err) {
            console.error('Update error:', err);
            if (err.response && err.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            } else if (err?.response?.data?.errors) {
                const errors = err.response.data.errors;
                const msg = Object.keys(errors).map(k => errors[k].join(' ')).join(' ');
                setError(msg);
            } else if (err?.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsSubmitted(false);
        }
    };

    return (
        <GoogleMapsProvider>
            <div className="container-fluid mt-5">
                <h2 className="text-center">
                    Edit Delivery Details{' '}
                    <Link to={`/pickups/${pickupId}`}>#{pickupId}</Link>
                    {user && (
                        <>
                            {' '}for{' '}
                            <Link to={`/users/${user.id}`}>
                                #{user.id} {user.company?.business_name || user.full_name}
                            </Link>
                        </>
                    )}
                </h2>

                {error && <div className="alert alert-danger">{error}</div>}

                {isLoading ? (
                    <div className="text-center"><Spinner animation="border" /></div>
                ) : (
                    <div className="row">
                        <div className="col-xl-6">
                            <div className="card">
                                <div className="card-body">
                                    <form
                                        onSubmit={handleSubmit}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        {routes.map((route) => (
                                            <div key={route.id} className="mb-3">
                                                <h5 className="d-flex justify-content-between">
                                                    <span>Route {route.id}</span>
                                                    {routes.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger ml-2 px-2 py-1"
                                                            onClick={() => handleRemoveRoute(route.id)}
                                                        >
                                                            <i className="fa fa-times" />
                                                        </button>
                                                    )}
                                                </h5>

                                                <div className="mb-3">
                                                    <NPPlacesInput
                                                        placeholder="Pickup Address"
                                                        value={route.origin.address || ''}
                                                        placeName={route.origin.place_name || ''}
                                                        enableMapPicker
                                                        location={route.origin}
                                                        locationType="pickup"
                                                        mapPinAriaLabel={`Choose pickup location for route ${route.id} on map`}
                                                        mapPinTestId={`route-${route.id}-pickup-map-pin`}
                                                        onAddressChange={(address) =>
                                                            handleLocationAddressChange(route.id, address, true)
                                                        }
                                                        onResultSelected={(address, location, placeName, isMapPin) =>
                                                            handlePickupSelect(route.id, address, location, placeName, isMapPin)
                                                        }
                                                        uniqueKey={`origin_address_${route.id}`}
                                                    />

                                                    <div className="d-flex mx-2 mt-3 mb-1">
                                                        <div>Contact person for pickup address</div>
                                                        <div className="px-4 mx-1">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`originUseMyself-${route.id}`}
                                                                checked={route.originUseMyself || false}
                                                                onChange={(e) => handleUseMyselfChange(route.id, true, e.target.checked)}
                                                            />
                                                            <label className="form-check-label" htmlFor={`originUseMyself-${route.id}`}>
                                                                Customer {user?.first_name && `(${user.first_name} ${user.last_name})`}
                                                            </label>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={route.origin.contact_name || ''}
                                                            onChange={(e) => handleRouteChange(route.id, 'contact_name', e.target.value, true)}
                                                            placeholder="Contact name"
                                                            className="form-control w-50 mr-2"
                                                        />

                                                        <InputMask
                                                            className="form-control w-50"
                                                            mask="+1 (999) 999-9999"
                                                            value={route.origin.contact_phone || ""}
                                                            placeholder="Contact phone"
                                                            onChange={(e) => {
                                                                let value = e.target.value.replace(/[^0-9]/g, "");
                                                                if (!value.startsWith("1")) value = "1" + value;
                                                                handleRouteChange(route.id, "contact_phone", `+${value}`, true);
                                                            }}
                                                            onBlur={(e) => {
                                                                if (!e.target.value || e.target.value === "+1") {
                                                                    handleRouteChange(route.id, "contact_phone", "+1", true);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                <NPPlacesInput
                                                    placeholder="Drop-off Address"
                                                    value={route.dropoff.address || ''}
                                                    placeName={route.dropoff.place_name || ''}
                                                    enableMapPicker
                                                    location={route.dropoff}
                                                    locationType="dropoff"
                                                    mapPinAriaLabel={`Choose drop-off location for route ${route.id} on map`}
                                                    mapPinTestId={`route-${route.id}-dropoff-map-pin`}
                                                    onAddressChange={(address) =>
                                                        handleLocationAddressChange(route.id, address, false)
                                                    }
                                                    onResultSelected={(address, location, placeName, isMapPin) =>
                                                        handleDropoffSelect(route.id, address, location, placeName, isMapPin)
                                                    }
                                                    uniqueKey={`dropoff_address_${route.id}`}
                                                />

                                                <div className="d-flex mx-2 mt-3 mb-1">
                                                    <div>Contact person for drop-off</div>
                                                    <div className="px-4 mx-1">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`dropoffUseMyself-${route.id}`}
                                                            checked={route.dropoffUseMyself || false}
                                                            onChange={(e) => handleUseMyselfChange(route.id, false, e.target.checked)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`dropoffUseMyself-${route.id}`}>
                                                            Customer {user?.first_name && `(${user.first_name} ${user.last_name})`}
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="d-flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={route.dropoff.contact_name || ''}
                                                        onChange={(e) => handleRouteChange(route.id, 'contact_name', e.target.value, false)}
                                                        placeholder="Contact name"
                                                        className="form-control w-50 mr-1"
                                                    />

                                                    <InputMask
                                                        className="form-control w-50"
                                                        mask="+9 (999) 999-9999"
                                                        value={route.dropoff.contact_phone || ""}
                                                        placeholder="Contact phone"
                                                        onChange={(e) => {
                                                            let value = e.target.value.replace(/[^0-9]/g, "");
                                                            if (!value.startsWith("1")) value = "1" + value;
                                                            handleRouteChange(route.id, "contact_phone", `+${value}`, false);
                                                        }}
                                                        onBlur={(e) => {
                                                            if (!e.target.value || e.target.value === "+1") {
                                                                handleRouteChange(route.id, "contact_phone", "+1", false);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}

                                        <button type="button" className="btn btn-secondary mb-2" onClick={handleAddRoute}>
                                            Add Route
                                        </button>

                                        <div className="form-row mb-1">
                                            <label className="col-sm-3 col-form-label">Truck Type:</label>
                                            <div className="col-sm-9">
                                                <select
                                                    className="custom-select"
                                                    value={truckType}
                                                    onChange={(e) => setTruckType(e.target.value)}
                                                >
                                                    <option>Select Truck Type</option>
                                                    <option value="1">Pickup truck</option>
                                                    <option value="2">Flatbed truck</option>
                                                    <option value="3">Cube Van</option>
                                                    <option value="4">Cargo Van</option>
                                                    <option value="11">Moffett</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="form-row mb-2">
                                            <label className="col-sm-3 col-form-label">Cargo Insurance:</label>
                                            <div className="col-sm-9">
                                                <select
                                                    className="custom-select"
                                                    value={cargoInsurance}
                                                    onChange={(e) => setCargoInsurance(e.target.value)}
                                                >
                                                    <option>Select Cargo Insurance</option>
                                                    <option value="1">$0 - $499</option>
                                                    <option value="2">$500 - $999</option>
                                                    <option value="3">$1000 - $1999</option>
                                                    <option value="4">$2000 - $2999</option>
                                                    <option value="5">$3000 - $5000</option>
                                                    <option value="6">N/A</option>
                                                </select>
                                            </div>
                                        </div>

                                        <textarea
                                            ref={additionalInfoRef}
                                            value={additionalInfo}
                                            className="form-control"
                                            name="add_info"
                                            onChange={(e) => setAdditionalInfo(e.target.value)}
                                            placeholder="Instruction for driver"
                                            rows={1}
                                        />

                                        <div className="form-group form-check mt-3">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="scheduledPickupCheckbox"
                                                checked={isScheduled}
                                                onChange={(e) => setIsScheduled(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="scheduledPickupCheckbox">
                                                Schedule Delivery
                                            </label>
                                        </div>

                                        {isScheduled && (
                                            <div className="form-group">
                                                <label htmlFor="scheduledPickupTime">Scheduled Delivery Time:</label>
                                                <input
                                                    type="datetime-local"
                                                    className="form-control"
                                                    id="scheduledPickupTime"
                                                    value={scheduledPickupTime}
                                                    onChange={(e) => setScheduledPickupTime(e.target.value)}
                                                    min={minDateTime}
                                                    required
                                                />
                                            </div>
                                        )}

                                        <hr />
                                        <h5 className="text-center">Items</h5>

                                        <table className="table table-sm table-borderless">
                                            <tbody>
                                            {items.map((item) => {
                                                const routeOptions = routes.map((r) => ({
                                                    value: r.id,
                                                    label: `Route ${r.id}`,
                                                }));

                                                return (
                                                    <React.Fragment key={item.id}>
                                                        <tr style={{ position: 'relative' }}>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    value={item.item_name}
                                                                    onChange={(e) => handleItemNameChange(item.id, e.target.value)}
                                                                    onFocus={() => setActiveItemId(item.id)}
                                                                    onBlur={() => {
                                                                        setTimeout(() => {
                                                                            setItemSuggestions([]);
                                                                            setActiveItemId(null);
                                                                        }, 200);
                                                                    }}
                                                                    placeholder="Item Name"
                                                                    className="form-control"
                                                                />

                                                                {activeItemId === item.id && itemSuggestions.length > 0 && (
                                                                    <ul
                                                                        className="list-group"
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: '100%',
                                                                            left: 0,
                                                                            right: 0,
                                                                            zIndex: 999,
                                                                            maxHeight: '300px',
                                                                            overflowY: 'auto',
                                                                            background: '#fff',
                                                                            border: '1px solid #d4d4d4',
                                                                        }}
                                                                    >
                                                                        {itemSuggestions.map((sugg) => (
                                                                            <li
                                                                                key={sugg.item_name}
                                                                                className="list-group-item list-group-item-action"
                                                                                style={{ cursor: 'pointer' }}
                                                                                onMouseDown={() => handleSuggestionSelect(item.id, sugg)}
                                                                            >
                                                                                {sugg.item_name} | {sugg.weight} | {sugg.size}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    value={item.weight}
                                                                    onChange={(e) => handleItemChange(item.id, 'weight', e.target.value)}
                                                                    placeholder="Weight"
                                                                    className="form-control"
                                                                />
                                                            </td>

                                                            <td>
                                                                <select
                                                                    value={item.size}
                                                                    onChange={(e) => handleItemChange(item.id, 'size', e.target.value)}
                                                                    className="custom-select"
                                                                >
                                                                    <option value="">Select Size</option>
                                                                    <option value="Small">Small</option>
                                                                    <option value="Medium">Medium</option>
                                                                    <option value="Large">Large</option>
                                                                    <option value="Extra Large">Extra Large</option>
                                                                </select>
                                                            </td>

                                                            <td>
                                                                <select
                                                                    value={item.routeId}
                                                                    onChange={(e) => handleItemRouteChange(item.id, parseInt(e.target.value))}
                                                                    className="custom-select"
                                                                    style={{ minWidth: '6rem' }}
                                                                >
                                                                    {routeOptions.map((opt) => (
                                                                        <option key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>

                                                            <td rowSpan="2">
                                                                {items.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-danger mt-1"
                                                                        onClick={() => handleRemoveItem(item.id)}
                                                                    >
                                                                        <i className="fa fa-trash" />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    value={item.purchase_order_number}
                                                                    onChange={(e) => handleItemChange(item.id, 'purchase_order_number', e.target.value)}
                                                                    placeholder="P.O.#"
                                                                    className="form-control"
                                                                />
                                                            </td>

                                                            <td colSpan="3">
                                                                    <textarea
                                                                        ref={(el) => { itemDetailsRefs.current[item.id] = el; }}
                                                                        value={item.details}
                                                                        onChange={(e) => handleItemChange(item.id, 'details', e.target.value)}
                                                                        placeholder="Eg. 4' x 5' x 5' fragile"
                                                                        className="form-control"
                                                                        rows={1}
                                                                    />
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td colSpan="4">
                                                                <input
                                                                    className="form-control"
                                                                    type="file"
                                                                    id={`formFile-${item.id}`}
                                                                    onChange={(e) => handleFileChange(item.id, e.target.files[0])}
                                                                />

                                                                {(photoFetchLoadingItemId === item.id || item.photoUrl) && (
                                                                    <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                                                                        {photoFetchLoadingItemId === item.id && (
                                                                            <div style={{
                                                                                position: 'absolute',
                                                                                top: '50%',
                                                                                left: '50%',
                                                                                transform: 'translate(-50%, -50%)'
                                                                            }}>
                                                                                <Spinner animation="border" className="text-primary" />
                                                                            </div>
                                                                        )}

                                                                        {item.photoUrl && (
                                                                            <div className="mt-2">
                                                                                <img
                                                                                    src={item.photoUrl}
                                                                                    alt="Preview"
                                                                                    style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover' }}
                                                                                />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td colSpan="5">
                                                                <hr className="my-0" />
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            })}
                                            </tbody>
                                        </table>

                                        <button type="button" className="btn btn-warning mb-2" onClick={handleAddItem}>
                                            Add Item
                                        </button>

                                        <hr />
                                        <div className="d-flex justify-content-center">
                                            <button className="btn btn-primary" disabled={isSubmitted}>
                                                {isSubmitted ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                                        &nbsp;Updating...
                                                    </>
                                                ) : (
                                                    'Update'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-6">
                            <div className="card">
                                <div className="card-body" style={{ minHeight: '400px' }}>
                                    <NPMapViewDirections
                                        origins={pickup.origins}
                                        dropoffs={pickup.dropoffs}
                                        onDistanceCalculated={setDistance}
                                        onWaypointsOptimized={handleWaypointsOptimized}
                                    />

                                    <div className="mt-2">Distance: {distance} km</div>

                                    {isEstimating && (
                                        <div className="text-center mb-2">
                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                            &nbsp;Estimating price...
                                        </div>
                                    )}

                                    {estimateError && <div className="alert alert-danger">{estimateError}</div>}

                                    {priceEstimate && (
                                        <div className="mt-2">
                                            <div className="table-responsive mb-4">
                                                <table className='table table-sm table-striped text-center mb-0'>
                                                    <tbody>
                                                    <tr>
                                                        <td>Delivery Fee (Delivery Cost)</td>
                                                        <td>${priceEstimate.delivery_cost}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Cargo Protection Pkg ({priceEstimate.cargo_insurance.insurance_range})</td>
                                                        <td>${priceEstimate.insurance_cost}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Cost (Delivery Fee + Cargo Protection Pkg)</td>
                                                        <td><b>${priceEstimate.total_cost}</b></td>
                                                    </tr>
                                                    <tr>
                                                        <td>GST {parseFloat(priceEstimate.tax_rate?.gst || 0)}%</td>
                                                        <td>${priceEstimate.gst_five_percent}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>PST {parseFloat(priceEstimate.tax_rate?.pst || 0)}%</td>
                                                        <td>${priceEstimate.pst_seven_percent}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>HST {parseFloat(priceEstimate.tax_rate?.hst || 0)}%</td>
                                                        <td>${priceEstimate.hst_amount}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>QST {parseFloat(priceEstimate.tax_rate?.qst || 0)}%</td>
                                                        <td>${priceEstimate.qst_amount}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Total Cost</td>
                                                        <td><b>${priceEstimate.grand_total}</b></td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="table-responsive">
                                                <table className="table table-sm table-striped text-center mb-0">
                                                    <tbody>
                                                    <tr className="table-light">
                                                        <td><h5 className="text-center mb-0">Driver Earnings</h5></td>
                                                        <td></td>
                                                    </tr>
                                                    <tr>
                                                        <td>Delivery Fee (Delivery Cost)</td>
                                                        <td>${priceEstimate.delivery_cost}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <Link to={"/truck-types/prices#truck-type-" + truckType} target="_blank">
                                                                Driver Compensation Rate ({priceEstimate.truck_type.title})
                                                            </Link>
                                                        </td>
                                                        <td>{priceEstimate.truck_type.driver_percent}%</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Driver Earnings</td>
                                                        <td><b>${priceEstimate.driver_earnings}</b></td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </GoogleMapsProvider>
    );
};

export default withAuth(PickupUpdate);
